class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    // Only set flags if the additional product element exists
    const additionalProductElement = document.querySelector('.additional-product');
    if (!additionalProductElement) {
      console.log('Additional product element does not exist, skipping flag setting');
      // Continue with normal rendering without additional product functionality
    } else {
      // Set a flag to prevent checking for additional products when the cart drawer is first shown
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        // Clear any existing timeout to prevent multiple resets
        if (cartDrawerItems.resetFlagTimeout) {
          clearTimeout(cartDrawerItems.resetFlagTimeout);
        }

        cartDrawerItems.isAddingAdditionalProduct = true;
        console.log('Setting isAddingAdditionalProduct flag to prevent additional product check on initial render');

        // Reset the flag after a longer delay to ensure all cart updates are complete
        cartDrawerItems.resetFlagTimeout = setTimeout(() => {
          cartDrawerItems.isAddingAdditionalProduct = false;
          console.log('Resetting isAddingAdditionalProduct flag after initial render');

          // Add a further delay before checking if we need to add the additional product
          // This prevents immediate add/remove cycles
          setTimeout(() => {
            // After resetting the flag, check if we need to add the additional product
            // This ensures we only do this check once after all updates are complete
            if (additionalProductElement) {
              const cartTotal = parsedState.total_price !== undefined ? parsedState.total_price : parseInt(additionalProductElement.dataset.cartTotal);
              const minimumTotal = parseInt(additionalProductElement.dataset.total);
              console.log('Cart Drawer: Checking if additional product should be added after reset. Cart total:', cartTotal, 'Minimum:', minimumTotal);

              // Only perform the check if we're above the minimum threshold
              if (cartTotal > minimumTotal) {
                cartDrawerItems.checkAndAddAdditionalProduct();
              }
            }
          }, 1000); // Add a 1-second delay before checking
        }, 2000); // Increased timeout to ensure all cart updates are complete
      }
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const effectiveSelector = selector || '.shopify-section';
    return new DOMParser().parseFromString(html, 'text/html').querySelector(effectiveSelector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: null,
      },
    ];
  }

  // Override the checkAndAddAdditionalProduct method to properly handle cart items in the drawer context
  async checkAndAddAdditionalProduct(cartData = null) {
    if (this.isCheckingAdditionalProduct) return;
    if (this.isAddingAdditionalProduct) return;

    this.isCheckingAdditionalProduct = true;

    try {
      const additionalProductElement = document.querySelector('.additional-product');
      if (!additionalProductElement) return;

      const standardProductId = additionalProductElement.dataset.product;
      const cooledProductId = additionalProductElement.dataset.productCooled;
      const minimumTotal = parseInt(additionalProductElement.dataset.total);

      if (!standardProductId && !cooledProductId) return;

      let cart = cartData;
      if (!cart) {
        const res = await fetch(`${routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } });
        cart = await res.json();
      }

      const items = cart.items || [];
      console.log('Cart Drawer Items:', items);

      const standardItem = standardProductId ? items.find((it) => String(it.id) === String(standardProductId)) : null;
      const cooledItem = cooledProductId ? items.find((it) => String(it.id) === String(cooledProductId)) : null;

      const standardExists = !!standardItem;
      const cooledExists = !!cooledItem;

      const standardPrice = standardItem ? parseInt(standardItem.price) : 0;
      const cooledPrice = cooledItem ? parseInt(cooledItem.price) : 0;

      const otherItems = items.filter((it) => String(it.id) !== String(standardProductId) && String(it.id) !== String(cooledProductId));
      console.log('Cart Drawer Other Items:', otherItems);

      const isOnlyDigital = otherItems.length > 0 && otherItems.every((it) => !it.requires_shipping);

      if (isOnlyDigital) {
        console.log('Cart Drawer is digital only');
        const toRemove = [];
        if (standardExists) toRemove.push(standardProductId);
        if (cooledExists) toRemove.push(cooledProductId);
        if (toRemove.length > 0) await this.removeAdditionalProducts(toRemove);
        return;
      }

      // Enforce quantity 1
      if (standardItem && standardItem.quantity > 1) {
        await this.updateQuantityByProductId(standardProductId, 1);
        return;
      }
      if (cooledItem && cooledItem.quantity > 1) {
        await this.updateQuantityByProductId(cooledProductId, 1);
        return;
      }

      const hasCooledProducts = otherItems.some(it => {
        const type = it.product_type ? it.product_type.toLowerCase().trim() : '';
        console.log(`Drawer Item ${it.title} type: ${it.product_type} (normalized: ${type})`);
        return type === 'cooled';
      });
      const hasOtherProducts = otherItems.some(it => !it.product_type || it.product_type.toLowerCase().trim() !== 'cooled');

      console.log('Cart Drawer Has Cooled Products:', hasCooledProducts);
      console.log('Cart Drawer Has Other Products:', hasOtherProducts);

      const cartTotalWithoutAdditional = parseInt(cart.total_price) - standardPrice - cooledPrice;
      console.log('Cart Drawer Total (net):', cartTotalWithoutAdditional, 'Minimum:', minimumTotal);

      const itemsToAdd = [];
      const itemsToRemove = [];

      // 1. Cooled Logic
      if (cooledProductId) {
        const shouldAddCooled = hasCooledProducts && (cartTotalWithoutAdditional > minimumTotal);
        console.log('Cart Drawer Should Add Cooled:', shouldAddCooled);
        if (shouldAddCooled) {
          if (!cooledExists) {
            console.log('Cart Drawer Adding Cooled Product:', cooledProductId);
            itemsToAdd.push(cooledProductId);
          }
        } else {
          if (cooledExists) {
            console.log('Cart Drawer Removing Cooled Product:', cooledProductId);
            itemsToRemove.push(cooledProductId);
          }
        }
      }

      // 2. Standard Logic
      if (standardProductId) {
        const shouldAddStandard = hasOtherProducts && (cartTotalWithoutAdditional > minimumTotal);
        console.log('Cart Drawer Should Add Standard:', shouldAddStandard);
        if (shouldAddStandard) {
          if (!standardExists) {
            console.log('Cart Drawer Adding Standard Product:', standardProductId);
            itemsToAdd.push(standardProductId);
          }
        } else {
          if (standardExists) {
            console.log('Cart Drawer Removing Standard Product:', standardProductId);
            itemsToRemove.push(standardProductId);
          }
        }
      }

      if (itemsToRemove.length > 0) {
        await this.removeAdditionalProducts(itemsToRemove);
      }

      if (itemsToAdd.length > 0) {
        await this.addAdditionalProducts(itemsToAdd);
      }

    } catch (e) {
      console.error('Cart Drawer: Error in checkAndAddAdditionalProduct:', e);
    } finally {
      this.isCheckingAdditionalProduct = false;
    }
  }

  async updateQuantityByProductId(productId, quantity) {
    const cartItemsDom = document.querySelectorAll('#CartDrawer-CartItems .cart-item');
    let lineItemIndex = null;
    cartItemsDom.forEach((item, index) => {
      if (item.dataset.additionalProductId === String(productId)) {
        lineItemIndex = index + 1;
      }
    });
    if (lineItemIndex !== null) {
      this.updateQuantity(lineItemIndex, quantity);
    }
  }

  // Override the addAdditionalProduct method to properly handle cart items in the drawer context
  async addAdditionalProducts(productIds) {
    if (!productIds || productIds.length === 0 || this.isAddingAdditionalProduct) return;

    this.isAddingAdditionalProduct = true;
    this.lastAdditionalProductAddTime = Date.now();

    try {
      const items = productIds.map(id => ({
        id: parseInt(id),
        quantity: 1
      }));

      const formData = {
        'items': items,
        'sections': this.getSectionsToRender().map((section) => section.section)
      };

      const response = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();

      if (data) {
        document.querySelector('cart-drawer').renderContents(data);
      } else {
        this.onCartUpdate();
      }
    } catch (error) {
      console.error('Cart Drawer: Error adding product to cart:', error);
      this.onCartUpdate();
    } finally {
      this.isAddingAdditionalProduct = false;
    }
  }

  // Override the removeAdditionalProduct method to properly handle cart items in the drawer context
  async removeAdditionalProduct(productId) {
    if (!productId || this.isAddingAdditionalProduct) return;

    const lastAddTime = this.lastAdditionalProductAddTime || 0;
    const currentTime = Date.now();

    if (currentTime - lastAddTime < 5000) {
      return;
    }

    this.isAddingAdditionalProduct = true;

    try {
      const cartItems = document.querySelectorAll('#CartDrawer-CartItems .cart-item');
      let lineItemIndex = null;

      cartItems.forEach((item, index) => {
        if (item.dataset.additionalProductId === String(productId)) {
          lineItemIndex = index + 1;
        }
      });

      if (lineItemIndex !== null) {
        this.updateQuantity(lineItemIndex, 0);
        // Short delay to prevent race conditions
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    } finally {
      this.isAddingAdditionalProduct = false;
    }
  }

  async removeAdditionalProducts(productIds) {
    if (!productIds || productIds.length === 0 || this.isAddingAdditionalProduct) return;

    const lastAddTime = this.lastAdditionalProductAddTime || 0;
    const currentTime = Date.now();

    if (currentTime - lastAddTime < 5000) {
      return;
    }

    this.isAddingAdditionalProduct = true;

    try {
      const linesToDisable = [];
      const cartItems = document.querySelectorAll('#CartDrawer-CartItems .cart-item');
      cartItems.forEach((item, index) => {
        if (productIds.includes(item.dataset.additionalProductId)) {
          const line = index + 1;
          this.enableLoading(line);
          linesToDisable.push(line);
        }
      });

      const updates = {};
      productIds.forEach((id) => {
        updates[id] = 0;
      });

      const body = JSON.stringify({
        updates: updates,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      const state = await response.text();
      const parsedState = JSON.parse(state);

      if (parsedState.errors) {
        return;
      }

      this.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartDrawerWrapper = document.querySelector('cart-drawer');
      const cartFooter = document.getElementById('main-cart-footer');

      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

      this.getSectionsToRender().forEach((section) => {
        const elementToReplace = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (elementToReplace && parsedState.sections[section.section]) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }
      });

      const additionalProductElement = document.querySelector('.additional-product');
      if (additionalProductElement) {
        additionalProductElement.dataset.cartTotal = parsedState.total_price;
      }

      // Short delay to prevent race conditions
      await new Promise(resolve => setTimeout(resolve, 2000));

      linesToDisable.forEach((line) => this.disableLoading(line));

    } catch (e) {
      console.error('Cart Drawer: Error removing products:', e);
      this.onCartUpdate();
    } finally {
      this.isAddingAdditionalProduct = false;
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
